import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  schema?: object;
}

export const useSEO = ({ title, description, keywords, canonicalPath, schema }: SEOProps) => {
  const location = useLocation();

  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: `${import.meta.env.VITE_SITE_URL || ''}${canonicalPath}` },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];

    metaTags.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let element = document.querySelector(selector);
      
      if (!element) {
        element = document.createElement('meta');
        if (name) element.setAttribute('name', name);
        if (property) element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    });

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || ''}${canonicalPath}`);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', today);

    // Update Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"][data-page-schema]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        schemaScript.setAttribute('data-page-schema', 'true');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }
  }, [title, description, keywords, canonicalPath, schema, location]);
};

// Schema.org generators
export const generateWebPageSchema = (name: string, description: string, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url: `${import.meta.env.VITE_SITE_URL || ''}${url}`,
  inLanguage: 'ja',
  isPartOf: {
    '@type': 'WebSite',
    name: 'おかやま小児科クリニック',
    url: import.meta.env.VITE_SITE_URL || '',
  },
});

export const generateMedicalClinicSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'MedicalClinic',
  name: 'おかやま小児科クリニック',
  description: '岡山市北区の小児科クリニック。一般診療、予防接種、乳幼児健診、アレルギー疾患に対応。',
  url: import.meta.env.VITE_SITE_URL || '',
  telephone: '+81-86-012-3456',
  faxNumber: '+81-86-012-3457',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '○○町1-2-3',
    addressLocality: '岡山市北区',
    addressRegion: '岡山県',
    postalCode: '700-0000',
    addressCountry: 'JP',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '34.66',
    longitude: '133.9',
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '18:00',
    },
  ],
  medicalSpecialty: 'Pediatric',
  priceRange: '$$',
  founder: {
    '@type': 'Person',
    name: '岡山太郎',
    jobTitle: '院長',
  },
});

export const generateFAQPageSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${import.meta.env.VITE_SITE_URL || ''}${item.url}`,
  })),
});
